import React, { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Responsive, WidthProvider } from 'react-grid-layout';
const ResponsiveGridLayout = WidthProvider(Responsive);
import { useElementSize } from '@mantine/hooks';
import { Container, Title, ScrollArea, Box, ThemeIcon, Modal, Group, Text, Divider, Stack, Button, Loader, Center } from '@mantine/core';
import { IconGripVertical, IconLock, IconRotateClockwise } from '@tabler/icons-react';

import BaseService from '../services/BaseService';
import useAuth from '../utils/useAuth';
import { translate } from '../utils/i18n';
import { fetchTasksByUser } from './Settings/store/myTaskSlice';
import { fetchTaskCounts } from './Settings/store/taskSlice';
import { fetchQuickTasksByUser } from './Settings/store/quickTaskSlice';
import { fatchLazytasksConfig } from './Settings/store/settingSlice';
import { hasPermission } from './ui/permissions';

import Onboarding from './Onboarding/Onboarding';
import DashboardCard from './Dashboard/DashboardCard';
import DashboardPieChart from './Dashboard/DashboardPieChart';
import DashboardTaskProgress from './Dashboard/DashboardTaskProgress';
import DashboardBarChart from './Dashboard/DashboardBarChart';
import TaskListTabs from './Dashboard/TaskListTabs';
import QuickTaskList from './Dashboard/QuickTaskList';
import UserActivities from './Dashboard/UserActivities';
import UserEngagementChart from './Dashboard/UserEngagementChart';
import ReviewWidget from './Dashboard/ReviewWidget';

import 'react-grid-layout/css/styles.css';
import 'react-resizable/css/styles.css';
import {fetchAllProjects} from "./Settings/store/projectSlice";

const WIDGETS_CONFIG = {
    'summary_total': { component: <DashboardCard type="total" /> },
    'summary_progress': { component: <DashboardCard type="progress" /> },
    'summary_completed': { component: <DashboardCard type="completed" /> },
    'summary_overdue': { component: <DashboardCard type="overdue" /> },
    'pie': { component: <DashboardPieChart /> },
    'progress': { component: <DashboardTaskProgress /> },
    'bar': { component: <DashboardBarChart /> },
    'tabs': { component: <TaskListTabs /> },
    'quick': { component: <QuickTaskList /> },
    'activity': { component: <UserActivities /> },
    'engagement': { component: <UserEngagementChart /> },
};

// Default Layout (4 Columns)
const DEFAULT_LAYOUT = [
    { i: 'summary_total', x: 0, y: 0, w: 1, h: 4, minW: 1, minH: 3 },
    { i: 'summary_progress', x: 1, y: 0, w: 1, h: 4, minW: 1, minH: 3 },
    { i: 'summary_completed', x: 2, y: 0, w: 1, h: 4, minW: 1, minH: 3 },
    { i: 'summary_overdue', x: 3, y: 0, w: 1, h: 4, minW: 1, minH: 3 },
    { i: 'pie', x: 0, y: 4, w: 2, h: 15, minW: 2, minH: 8 },
    { i: 'progress', x: 2, y: 4, w: 2, h: 15, minW: 2, minH: 8 },
    { i: 'bar', x: 0, y: 19, w: 2, h: 12, minW: 2, minH: 8 },
    { i: 'tabs', x: 2, y: 19, w: 2, h: 12, minW: 2, minH: 8 },
    { i: 'engagement', x: 0, y: 31, w: 2, h: 12, minW: 2, minH: 8 },
    { i: 'quick', x: 2, y: 31, w: 1, h: 12, minW: 1, minH: 8 },
    { i: 'activity', x: 3, y: 31, w: 1, h: 12, minW: 1, minH: 8 },
];

const Dashboard = () => {

    const dispatch = useDispatch();
    const { signOut } = useAuth();
    const { loggedUserId } = useSelector((state) => state.auth.user)
    const { token, loggedInUser, xWpNonce } = useSelector((state) => state.auth.session);
    const { lazytasksConfig } = useSelector((state) => state.settings.setting);

    const [config, setConfig] = useState(lazytasksConfig);
    const [notEngagedModalOpen, setNotEngagedModalOpen] = useState(false);
    const [resetModalOpen, setResetModalOpen] = useState(false);
    const [customizationModalOpen, setCustomizationModalOpen] = useState(false);
    const [isCustomizationMode, setIsCustomizationMode] = useState(false);
    const [wiggle, setWiggle] = useState(false);

    // Layout State
    const [layout, setLayout] = useState(DEFAULT_LAYOUT);
    const [isLayoutLoaded, setIsLayoutLoaded] = useState(false);
    const [projectsLoaded, setProjectsLoaded] = useState(false);
    const [hasProjectWithAssignedMembers, setHasProjectWithAssignedMembers] = useState(false);
    const isAdminUser = Boolean(appLocalizer?.is_admin) || (Array.isArray(loggedInUser?.roles) && loggedInUser.roles.includes('administrator'));
    // Grid Layout Width
    const { ref: containerRef, width } = useElementSize();

    useEffect(() => {
        if (lazytasksConfig) {
            setConfig(lazytasksConfig);
        }
    }, [lazytasksConfig]);

    useEffect(() => {
        if (!xWpNonce) return;

        const userId =
            loggedInUser?.loggedUserId ?? loggedUserId;

        const canFetchConfig =
            loggedInUser && hasPermission(loggedInUser, ['general-settings'], null, 'global');

        const fetchData = async () => {
            try {
                if (userId) {
                    dispatch(fetchTasksByUser({ id: userId }));
                    dispatch(fetchQuickTasksByUser({ id: userId }));
                    dispatch(fetchTaskCounts());
                }

                if (isAdminUser) {
                    const response = await dispatch(fetchAllProjects());
                    const projectList = Array.isArray(response?.payload?.data)
                        ? response.payload.data
                        : [];
                    const hasAssignedMembers =
                        projectList.length > 0 &&
                        projectList.some((project) => Array.isArray(project?.members) && project.members.length > 0);
                    setHasProjectWithAssignedMembers(hasAssignedMembers);
                    setProjectsLoaded(true);
                }

                if (canFetchConfig) {
                    const response = await dispatch(fatchLazytasksConfig());

                    if (response?.payload?.status === 200) {
                        setConfig(response.payload.data);
                    }
                }

                // Fetch saved layout
                const layoutResponse = await BaseService.get('/dashboard/layout', { baseURL: `${appLocalizer?.apiUrl}/lazytasks/api/v3` });
                if (layoutResponse.data && layoutResponse.data.status === 200 && Array.isArray(layoutResponse.data.data) && layoutResponse.data.data.length > 0) {
                    const savedLayout = layoutResponse.data.data;
                    // Validate if saved layout has correct structure (objects with 'i')
                    if (savedLayout[0] && typeof savedLayout[0] === 'object' && savedLayout[0].hasOwnProperty('i')) {
                        // Check for legacy 'summary' widget and reset if found to ensure new widgets appear at top
                        const hasLegacySummary = savedLayout.some(item => item.i === 'summary');

                        if (hasLegacySummary) {
                            console.log("Legacy 'summary' widget detected. Resetting to default layout.");
                            setLayout(DEFAULT_LAYOUT);
                            setIsLayoutLoaded(true);
                            return;
                        }

                        // Merge logic: Ensure all widgets in DEFAULT_LAYOUT are present
                        const validSavedLayout = savedLayout.filter(item => WIDGETS_CONFIG[item.i]);

                        // Enforce updated minH/minW from DEFAULT_LAYOUT on saved items
                        const updatedSavedLayout = validSavedLayout.map(savedItem => {
                            const defaultItem = DEFAULT_LAYOUT.find(d => d.i === savedItem.i);
                            if (defaultItem) {
                                // Update minH and minW if they differ or are missing
                                return {
                                    ...savedItem,
                                    minW: defaultItem.minW,
                                    minH: defaultItem.minH,
                                    // Ensure current dimensions respect new minimums
                                    w: Math.max(savedItem.w, defaultItem.minW),
                                    h: Math.max(savedItem.h, defaultItem.minH)
                                };
                            }
                            return savedItem;
                        });

                        // Find missing widgets
                        const savedIds = updatedSavedLayout.map(item => item.i);
                        const missingWidgets = DEFAULT_LAYOUT.filter(item => !savedIds.includes(item.i));

                        // If we need to put missing widgets somewhere, we place them at the bottom
                        const maxY = updatedSavedLayout.reduce((max, item) => Math.max(max, item.y + item.h), 0);

                        const newWidgets = missingWidgets.map((item, index) => ({
                            ...item,
                            y: maxY + (index * item.h) // Simple stacking for new widgets
                        }));

                        setLayout([...updatedSavedLayout, ...newWidgets]);
                    } else {
                        // Legacy layout or invalid - reset to default
                        console.warn("Invalid or legacy layout detected. Resetting to default.");
                        setLayout(DEFAULT_LAYOUT);
                    }
                }
                setIsLayoutLoaded(true);
            } catch (error) {
                console.error('Unexpected error:', error);
                setIsLayoutLoaded(true);
            }
        };
        fetchData();
    }, [
        dispatch,
        xWpNonce,
        loggedInUser
    ]);

    const shouldOpenOnboardingModal =
        Boolean(config?.lazytasks_basic_info_guide_modal) &&
        isAdminUser &&
        projectsLoaded &&
        !hasProjectWithAssignedMembers;
    useEffect(() => {
        if (loggedInUser) {
            const wpRoles = Array.isArray(loggedInUser.roles) ? loggedInUser.roles : [];
            const roles = Array.isArray(loggedInUser.llc_roles) ? loggedInUser.llc_roles : [];
            const perms = Array.isArray(loggedInUser.llc_permissions) ? loggedInUser.llc_permissions : [];
            const projectRoles = Array.isArray(loggedInUser.project_roles) ? loggedInUser.project_roles : [];

            const hasRoles = roles.length > 0;
            const hasProjectRoles = projectRoles.length > 0;

            // Remove "view-only-access" before checking
            const filteredPerms = perms.filter(p => p !== "view-only-access");
            const hasValidPermissions = filteredPerms.length > 0;
            const isWpAdmin = wpRoles.includes('administrator');

            const shouldOpen = !hasRoles && !hasValidPermissions && !hasProjectRoles && !isWpAdmin;

            setNotEngagedModalOpen(shouldOpen);
        }
    }, [loggedInUser, loggedUserId]);

    const handleGoBack = async () => {
        try {
            await signOut(); // wait for sign out to complete
        } catch (err) {
            // swallow or log if needed
            console.error('Sign out failed', err);
        } finally {
            const redirectUrl = appLocalizer?.home_url || '/';
            window.location.href = redirectUrl;
        }
    };

    const onLayoutChange = (currentLayout) => {
        // Only save if layout has actually loaded to prevent overwriting with default on init
        if (!isLayoutLoaded) return;

        setLayout(currentLayout);
        // Save Layout
        // Clean layout object to only save necessary fields
        const cleanLayout = currentLayout.map(({ i, x, y, w, h, minW, minH }) => ({ i, x, y, w, h, minW, minH }));

        BaseService.post('/dashboard/layout', { layout: cleanLayout }, { baseURL: `${appLocalizer?.apiUrl}/lazytasks/api/v3` })
            .catch(err => console.error("Failed to save layout", err));
    };

    const handleResetLayout = () => {
        setLayout(DEFAULT_LAYOUT);
        onLayoutChange(DEFAULT_LAYOUT);
        setIsCustomizationMode(false);
        setResetModalOpen(false);
    };

    const handleEnableCustomization = () => {
        setCustomizationModalOpen(false);
        setIsCustomizationMode(true);
        setWiggle(true);
        setTimeout(() => setWiggle(false), 1000); // Wiggle for 1 second
    };

    return (
        <Fragment>
            {/*<Header /> */}

            <div className='dashboard'>
                <Container size="full">
                    <div className="settings-page-card bg-white rounded-xl p-5 pt-3 mt-5 mb-0">
                        <div className='mt-2 mb-3'>
                            {/* <Title order={4}>{ __('Dashboard', 'lazytasks-project-task-management') }</Title> */}
                            <Group position="apart" justify="space-between">
                                <Title order={4}>{translate('Dashboard')}</Title>
                                {!isCustomizationMode ? (
                                    <Button
                                        variant="filled"
                                        color="orange"
                                        size="xs"
                                        style={{ color: 'white' }}
                                        onClick={() => setCustomizationModalOpen(true)}
                                    >
                                        {translate('Customize Dashboard')}
                                    </Button>
                                ) : (
                                    <Button
                                        leftSection={<IconRotateClockwise size={16} />}
                                        variant="light"
                                        color="red"
                                        size="xs"
                                        onClick={() => setResetModalOpen(true)}
                                    >
                                        {translate('Reset Layout')}
                                    </Button>
                                )}
                            </Group>
                        </div>

                        <ReviewWidget />

                        <ScrollArea scrollbars="y" className={`w-full px-2 ${appLocalizer?.is_admin ? 'h-[calc(100vh-250px)]' : 'h-[calc(100vh-190px)]'} overflow-hidden`}
                                    scrollbarSize={4}
                        >
                            <div ref={containerRef} style={{ width: '100%', minHeight: '400px' }}>
                                {!isLayoutLoaded || width === 0 ? (
                                    <Center h={400} w="100%">
                                        <Loader size="lg" />
                                    </Center>
                                ) : (
                                    <ResponsiveGridLayout
                                        className="layout"
                                        width={width}
                                        layouts={{
                                            lg: layout.map(item => ({
                                                ...item,
                                                static: !isCustomizationMode
                                            }))
                                        }}
                                        breakpoints={{ lg: 1200, md: 996, sm: 768, xs: 480, xxs: 0 }}
                                        cols={{ lg: 4, md: 4, sm: 2, xs: 1, xxs: 1 }}
                                        rowHeight={30}
                                        onLayoutChange={onLayoutChange}
                                        draggableHandle=".draggable-header"
                                        isDraggable={isCustomizationMode}
                                        isResizable={isCustomizationMode}
                                    >
                                        {layout.map(item => {
                                            const widget = WIDGETS_CONFIG[item.i];
                                            if (!widget) return null;

                                            const dragHandle = (
                                                <div
                                                    className="drag-handle"
                                                    style={{
                                                        cursor: 'grab',
                                                        opacity: 0.5,
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        padding: '4px',
                                                        borderRadius: '4px',
                                                    }}
                                                >
                                                    <ThemeIcon variant="transparent" color="gray" size="sm">
                                                        <IconGripVertical size={16} />
                                                    </ThemeIcon>
                                                </div>
                                            );

                                            return (
                                                <div key={item.i}>
                                                    <Box
                                                        pos="relative"
                                                        h="100%"
                                                        style={{ overflow: 'hidden' }}
                                                        className={wiggle ? 'wiggle' : ''}
                                                    >
                                                        {React.cloneElement(widget.component, { dragHandle: isCustomizationMode ? dragHandle : null })}
                                                    </Box>
                                                </div>
                                            );
                                        })}
                                    </ResponsiveGridLayout>
                                )}
                            </div>
                        </ScrollArea>
                    </div>
                </Container>

                <Modal
                    opened={shouldOpenOnboardingModal}
                    onClose={() => setConfig({ ...config, lazytasks_basic_info_guide_modal: false })}
                    title=""
                    size="auto"
                    // scrollAreaComponent={ScrollArea.Autosize}
                    withCloseButton={false}
                    closeOnClickOutside={false}
                    centered
                >
                    <Onboarding />
                </Modal>

                {notEngagedModalOpen && (
                    <Modal
                        opened={notEngagedModalOpen}
                        title={
                            <>
                                <Group spacing="xs">
                                    <ThemeIcon color="orange" radius="xl" size="lg" variant="filled">
                                        <IconLock size={24} />
                                    </ThemeIcon>
                                    <Text size="md" weight={500}>
                                        {translate('You are not assigned to any project in LazyTask!')}
                                    </Text>
                                </Group>
                            </>
                        }
                        size="md"
                        withCloseButton={false}
                        closeOnClickOutside={false}
                        returnFocus={false}
                        centered
                        overlayProps={{
                            color: 'rgba(0, 0, 0, 0.85)', // dark semi-transparent background
                            blur: 3,                      // slightly blurred background
                            opacity: 1,                 // more visible dark overlay
                            zIndex: 200,
                        }}
                    >
                        <Divider size="xs" my={0} className='!-ml-4 w-[calc(100%+2rem)]' />
                        <Stack spacing="md" pt="md">
                            <Text size="sm" ta="center" pt={5} c="#4D4D4D">
                                {translate('You are currently added as a user in WordPress, but you are not assigned to any project in the LazyTask.')} <br />
                                <strong>
                                    {translate('To start working in LazyTask, please contact the admin so they can assign you to a project.')}
                                </strong>
                            </Text>
                            <Group mt="sm" justify="center">
                                <Button
                                    variant='filled'
                                    color='orange'
                                    tabIndex={-1}
                                    onClick={handleGoBack}
                                    style={{ outline: 'none', boxShadow: 'none' }}
                                    fullWidth
                                >
                                    {translate('Back to Home')}
                                </Button>
                            </Group>
                        </Stack>
                    </Modal>
                )}

                <Modal
                    opened={customizationModalOpen}
                    onClose={() => setCustomizationModalOpen(false)}
                    title={translate('Customize Dashboard')}
                    centered
                >
                    <Text size="sm">
                        {translate('You can now customize the layout, allowing you to drag and drop or resize widgets. Are you sure you want to proceed?')}
                    </Text>
                    <Group justify="flex-end" mt="md">
                        <Button variant="default" onClick={() => setCustomizationModalOpen(false)}>
                            {translate('Cancel')}
                        </Button>
                        <Button color="orange" onClick={handleEnableCustomization}>
                            {translate('Yes, Customize')}
                        </Button>
                    </Group>
                </Modal>

                <Modal
                    opened={resetModalOpen}
                    onClose={() => setResetModalOpen(false)}
                    title={translate('Reset Dashboard Layout')}
                    centered
                >
                    <Text size="sm">
                        {translate('Are you sure you want to reset the dashboard layout to its default state? This action cannot be undone.')}
                    </Text>
                    <Group justify="flex-end" mt="md">
                        <Button variant="default" onClick={() => setResetModalOpen(false)}>
                            {translate('Cancel')}
                        </Button>
                        <Button color="red" onClick={handleResetLayout}>
                            {translate('Yes, Reset')}
                        </Button>
                    </Group>
                </Modal>

            </div>

            {/* <Footer /> */}
        </Fragment>

    );
}

export default Dashboard;
